import { Link } from "@tanstack/react-router";
import type { NavigationSection } from "@/shared/types/navigationTypes";
import { usePermissions } from "@/entities/session/hooks/usePermissions";
import { hasAnyPermission } from "@/shared/config/permissionLogic";
import { SidebarUserMenu } from "./SidebarUserMenu";
import type { MyProfileResponse } from "@/entities/employee/model/types";

type MainSidebarProps = {
  sections: NavigationSection[];
  activeSection: NavigationSection | undefined;
  currentUser: MyProfileResponse | undefined;
  logoUrl: string | null;
};

export function MainSidebar({ sections, activeSection, currentUser, logoUrl }: MainSidebarProps) {
  const { hasPermission: checkPermission } = usePermissions();

  return (
    <aside className="fixed left-0 top-0 z-50 flex h-screen w-20 flex-col border-r border-line bg-surface">
      <div className="flex items-center justify-center border-b border-line py-4">
        <Link to="/" className="flex items-center justify-center" aria-label="Home">
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-white p-1.5 ring-1 ring-black/5">
            <img
              src={logoUrl ?? "/logo-mark.svg"}
              alt={logoUrl ? "Workspace logo" : "Neztrack"}
              className="h-full w-full object-contain"
            />
          </span>
        </Link>
      </div>

      <nav className="flex flex-1 flex-col items-center gap-1 py-3">
        {sections.map(section => {
          const grantedBySection = hasAnyPermission(checkPermission, section.permission);
          const grantedByChild = section.subItems?.some(
            item => hasAnyPermission(checkPermission, item.permission),
          );
          if (!grantedBySection && !grantedByChild) return null;
          const isActive = section.id === activeSection?.id;
          const path = section.path ?? "#";
          return (
            <Link key={section.id} to={path} className="block">
              <NavigationButton section={section} isActive={isActive} />
            </Link>
          );
        })}
      </nav>

      <div className="flex items-center justify-center border-t border-line py-3">
        <SidebarUserMenu currentUser={currentUser} />
      </div>
    </aside>
  );
}

function NavigationButton({
  section,
  isActive,
}: {
  section: NavigationSection;
  isActive: boolean;
}) {
  const Icon = section.icon;
  return (
    <button
      className={`group relative flex h-10 w-10 items-center justify-center rounded-md transition-colors ${
        isActive
          ? "bg-brand-accent-soft text-brand-accent"
          : "text-ink-3 hover:bg-hover hover:text-ink-2"
      }`}
      aria-label={section.label}
    >
      <Icon size={18} strokeWidth={1.6} />
      <span className="pointer-events-none absolute left-full ml-3 hidden whitespace-nowrap rounded-md border border-line bg-surface px-2 py-1 text-xs font-medium text-ink-2 opacity-0 shadow-md transition-opacity group-hover:block group-hover:opacity-100">
        {section.label}
      </span>
    </button>
  );
}
